import imagesLoaded from 'imagesloaded';
import lightGallery from 'lightgallery';

const licenseKey = 'C789E420-34844F09-94EB07AB-DE2E7417';

function initialiseLightGallery(container) {
    const galleryElement = container.querySelector('.gallery-images-js');
    if (!galleryElement) return;

    const setLightGallery = (el) => {
        return lightGallery(el, {
            plugins: [],
            speed: 500,
            licenseKey: licenseKey,
            zoomFromOrigin: true,
            selector: '.showing',
        });
    };

    const setActiveButton = (button, buttons) => {
        buttons.forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');
    };

    const setActiveOption = (option, options) => {
        options.forEach((opt) => opt.removeAttribute('selected'));
        option.setAttribute('selected', '');
    };

    let lg = setLightGallery(galleryElement);

    const filterButtons = container.querySelectorAll('.filter-buttons > button');
    const select = container.querySelector('.filter-select');
    const options = select ? select.querySelectorAll('option') : [];

    const applyFilter = (filterTrigger) => {
        filterImages(container, filterTrigger);
        if (filterTrigger.tagName === 'BUTTON') {
            setActiveButton(filterTrigger, filterButtons);
        } else if (filterTrigger.tagName === 'OPTION') {
            setActiveOption(filterTrigger, options);
        }
        lg.destroy();
        lg = setLightGallery(galleryElement);
    };

    filterButtons.forEach((btn) => {
        if (btn.dataset.filter_no === '0') applyFilter(btn);
        btn.addEventListener('click', () => applyFilter(btn));
    });

    if (select) {
        select.addEventListener('change', () => {
            applyFilter(select.options[select.selectedIndex]);
        });

        options.forEach((opt) => {
            if (opt.dataset.filter_no === '0') applyFilter(opt);
        });
    }

    if (!select && filterButtons.length === 0) {
        showAllImages(container);
        lg.destroy();
        lg = setLightGallery(galleryElement);
    }
}

function initialiseDynamicLightGallery() {
    document.querySelectorAll('.thumbnail-gallery').forEach((card) => {
        const galleryButton = card.querySelector('.gallery-cta');
        if (!galleryButton) return;

        const dynamicElements = Array.from(card.querySelectorAll('.card-image')).map((img) => ({
            src: img.dataset.src,
            thumb: img.dataset.src,
            subHtml: img.dataset.description,
        }));

        if (dynamicElements.length > 0) {
            const dynamicGallery = lightGallery(galleryButton, {
                licenseKey: licenseKey,
                plugins: [lgThumbnail],
                dynamic: true,
                dynamicEl: dynamicElements,
                thumbnail: true,
            });

            galleryButton.addEventListener('click', () => {
                dynamicGallery.openGallery();
            });
        }
    });
}

function initialiseMasonry(container) {
    const masonryGrid = container.querySelector('.masonry-grid');
    if (!masonryGrid) return;

    const filterButtons = container.querySelectorAll('.filter-buttons > button');
    const selects = container.querySelectorAll('.filter-select');

    const reinit = () => new Masonry(masonryGrid, { itemSelector: '.showing' });

    let masonry = reinit();

    const layoutOnImagesLoaded = () => {
        imagesLoaded(masonryGrid).on('progress', () => {
            masonry.layout();
        });
    };

    layoutOnImagesLoaded();

    const reload = () => {
        masonry.destroy();
        masonry = reinit();
        masonry.reloadItems();
        layoutOnImagesLoaded();
    };

    masonryGrid.reloadMasonryLayout = reload;

    filterButtons.forEach((btn) => btn.addEventListener('click', reload));
    selects.forEach((sel) => sel.addEventListener('change', reload));
}

function showAllImages(container) {
    container.querySelectorAll('.gallery-image').forEach((img) => {
        img.style.display = 'block';
        img.classList.add('showing');
    });
}

function filterImages(container, trigger) {
    const selectedFilter = trigger.dataset.filter;
    container.querySelectorAll('.gallery-image').forEach((img) => {
        const filters = JSON.parse(img.dataset.filters);
        const match = selectedFilter === 'none' || filters.includes(selectedFilter);

        img.style.display = match ? 'block' : 'none';
        img.classList.toggle('showing', match);
    });
}

export default function initGallery() {
    const containers = document.querySelectorAll('.gallery-container');

    containers.forEach((container) => {
        const spinner = container.querySelector('#loading-spinner');

        initialiseLightGallery(container);
        initialiseMasonry(container);
        if (spinner) spinner.style.display = 'none';
    });

    initialiseDynamicLightGallery();

    // Visibility observer + retry helper:
    // Watches each gallery container and triggers the reload function exposed
    // on the internal masonry grid (masonryGrid.reloadMasonryLayout) when the
    // container becomes visible. This moves the retry logic into the module
    // so we don't need inline Blade scripts or Alpine.
    if ('IntersectionObserver' in window) {
        const retryCall = (container, attempts = 0) => {
            const masonryGrid = container.querySelector('.masonry-grid');
            if (masonryGrid && typeof masonryGrid.reloadMasonryLayout === 'function') {
                masonryGrid.reloadMasonryLayout();
                return true;
            }

            if (attempts < 6) {
                // retry after a short delay (200ms * attempts)
                setTimeout(() => retryCall(container, attempts + 1), 200);
            }
            return false;
        };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;

                const container = entry.target;
                // attempt immediate call; retryCall will poll if needed
                retryCall(container);

                // only need to do this once for each container
                obs.unobserve(container);
            });
        }, { root: null, threshold: 0.05 });

        containers.forEach((c) => observer.observe(c));
    }
}
